import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Api from "../../api";
import {
  FaServer,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTools,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import dayjs from "dayjs";
import "dayjs/locale/id";
import AddPerawatanIt from "../../components/perawatanIt/AddPerawatanIt";
import DataPerawatanIt from "../../components/perawatanIt/DataPerawatanIt";
import { SipDropdown, SipSearch } from "../../components/form/SipControls";
import { getChecklistTemplate } from "../../components/perawatanIt/checklistTemplates";
import { prepareImageForUpload } from "../../utils/imageCompress";

dayjs.locale("id");

const emptyForm = (petugas = "") => ({
  id: "",
  tanggal: dayjs().format("YYYY-MM-DD"),
  kategori_id: "",
  sumber_aset: "inventaris",
  inventaris_id: "",
  aset_manual_nama: "",
  aset_manual_lokasi: "",
  id_layanan: "",
  checklist: getChecklistTemplate(),
  catatan: "",
  status: "dicek",
  nama_petugas: petugas,
  foto: null,
  buat_perbaikan: false,
  jenis_kerusakan: "",
  tindakan: "",
});

const PerawatanIt = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("semua");
  const [filterTahun, setFilterTahun] = useState(
    new Date().getFullYear().toString()
  );
  const [loading, setLoading] = useState(false);
  const [loadingOpsi, setLoadingOpsi] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [rows, setRows] = useState([]);
  const [kategoriOptions, setKategoriOptions] = useState([]);
  const [layananOptions, setLayananOptions] = useState([]);
  const [inventarisOptions, setInventarisOptions] = useState([]);
  const [form, setForm] = useState(() => emptyForm(currentUser?.name || ""));
  const searchTimer = useRef(null);

  const resetForm = () => {
    setForm(emptyForm(currentUser?.name || ""));
    setIsEditMode(false);
    setLayananOptions([]);
  };

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Api.get("/mon/perawatan-it", {
        params: { per_page: 200 },
      });
      setRows(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Gagal memuat data perawatan IT");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadKategori = useCallback(async () => {
    try {
      setLoadingOpsi(true);
      const res = await Api.get("/mon/perawatan-it/opsi/kategori");
      setKategoriOptions(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Gagal memuat kategori IT");
    } finally {
      setLoadingOpsi(false);
    }
  }, []);

  const loadLayanan = async (kategoriId) => {
    if (!kategoriId) {
      setLayananOptions([]);
      return;
    }
    try {
      const lay = await Api.get("/mon/perawatan-it/opsi/layanan", {
        params: { kategori_id: kategoriId },
      });
      setLayananOptions(lay.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadInventaris = useCallback(async (search = "") => {
    try {
      setLoadingOpsi(true);
      const res = await Api.get("/mon/perawatan-it/opsi/inventaris", {
        params: { search },
      });
      setInventarisOptions(res.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOpsi(false);
    }
  }, []);

  const onInventarisSearch = (filterText) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      loadInventaris(filterText || "");
    }, 300);
  };

  useEffect(() => {
    getData();
    loadKategori();
    loadInventaris("");
  }, [getData, loadKategori, loadInventaris]);

  const onKategoriChange = async (kategoriId) => {
    await loadLayanan(kategoriId);
  };

  const postData = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("tanggal", form.tanggal);
      fd.append("kategori_id", form.kategori_id);
      fd.append("sumber_aset", form.sumber_aset || "inventaris");
      if (form.sumber_aset === "inventaris" && form.inventaris_id) {
        fd.append("inventaris_id", form.inventaris_id);
      }
      if (form.sumber_aset === "manual") {
        if (form.aset_manual_nama)
          fd.append("aset_manual_nama", form.aset_manual_nama);
        if (form.aset_manual_lokasi)
          fd.append("aset_manual_lokasi", form.aset_manual_lokasi);
      }
      if (form.id_layanan) fd.append("id_layanan", form.id_layanan);
      fd.append("checklist", JSON.stringify(form.checklist || []));
      fd.append("catatan", form.catatan || "");
      fd.append("status", form.status);
      fd.append("nama_petugas", form.nama_petugas || currentUser?.name || "");
      fd.append("buat_perbaikan", form.buat_perbaikan ? "1" : "0");
      if (form.jenis_kerusakan)
        fd.append("jenis_kerusakan", form.jenis_kerusakan);
      if (form.tindakan) fd.append("tindakan", form.tindakan);

      if (form.foto instanceof File) {
        const foto = await prepareImageForUpload(form.foto);
        if (foto) fd.append("foto", foto);
      }

      let res;
      if (isEditMode && form.id) {
        res = await Api.put(`/mon/perawatan-it/${form.id}`, fd);
        toast.success("Data perawatan IT berhasil diperbarui");
      } else {
        res = await Api.post("/mon/perawatan-it", fd);
        toast.success("Data perawatan IT berhasil disimpan");
      }

      if (res.data?.follow_up?.warning) {
        toast.warn(res.data.follow_up.warning);
      } else if (res.data?.follow_up?.id_perbaikan) {
        toast.info(
          `Tindak lanjut perbaikan dibuat (#${res.data.follow_up.id_perbaikan})`
        );
      }

      await getData();
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        (isEditMode
          ? "Gagal memperbarui data perawatan IT"
          : "Gagal menyimpan data perawatan IT");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const res = await Api.get(`/mon/perawatan-it/${id}`);
      const data = res.data?.data;
      if (!data) {
        toast.error("Data tidak ditemukan");
        return;
      }
      await loadLayanan(data.kategori_id);
      if (data.inventaris_id) {
        await loadInventaris(data.inventaris_kode || "");
      }
      const sumber = data.inventaris_id
        ? "inventaris"
        : data.aset_manual_nama
          ? "manual"
          : "inventaris";
      setForm({
        id: data.id,
        tanggal: data.tanggal
          ? dayjs(data.tanggal).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        kategori_id: data.kategori_id || "",
        sumber_aset: sumber,
        inventaris_id: data.inventaris_id || "",
        aset_manual_nama: data.aset_manual_nama || "",
        aset_manual_lokasi: data.aset_manual_lokasi || "",
        id_layanan: data.id_layanan || "",
        checklist: Array.isArray(data.checklist)
          ? data.checklist
          : getChecklistTemplate(data.kategori_kode),
        catatan: data.catatan || "",
        status: data.status || "dicek",
        nama_petugas: data.nama_petugas || currentUser?.name || "",
        foto: data.foto || null,
        buat_perbaikan: false,
        jenis_kerusakan: "",
        tindakan: "",
      });
      setIsEditMode(true);
      setIsModalOpen(true);
    } catch (err) {
      console.error(err);
      toast.error("Gagal mengambil data untuk diedit");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus data perawatan ini?")) return;
    try {
      setLoading(true);
      await Api.delete(`/mon/perawatan-it/${id}`);
      toast.success("Data berhasil dihapus");
      await getData();
    } catch (err) {
      console.error(err);
      toast.error("Gagal menghapus data");
    } finally {
      setLoading(false);
    }
  };

  const getAvailableYears = () => {
    const years = (rows || [])
      .map((item) => (item.tanggal ? dayjs(item.tanggal).year() : null))
      .filter(Boolean);
    return [...new Set(years)].sort((a, b) => b - a);
  };

  const filteredData = (rows || []).filter((item) => {
    const q = searchTerm.toLowerCase();
    const hay = [
      item.kategori_nama,
      item.aset_nama,
      item.aset_kode,
      item.aset_manual_nama,
      item.inventaris_nama,
      item.inventaris_kode,
      item.nama_layanan,
      item.nama_petugas,
      item.catatan,
      item.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesSearch = !q || hay.includes(q);
    const matchesStatus =
      filterStatus === "semua" || item.status === filterStatus;
    const matchesTahun =
      filterTahun === "semua" ||
      (item.tanggal && dayjs(item.tanggal).year().toString() === filterTahun);
    return matchesSearch && matchesStatus && matchesTahun;
  });

  return (
    <div className="space-y-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-primary px-6 py-8 md:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FaServer className="text-xl" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold">
                    Perawatan Perangkat IT
                  </h1>
                </div>
                <p className="text-primary text-sm md:text-base opacity-90">
                  Aset dari inventaris atau input manual + layanan IT
                </p>
              </div>
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-4 py-3 rounded-xl hover:bg-white/20 transition-all"
              >
                <span className="font-medium">Kembali</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {filteredData.length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <FaTools className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Dicek</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {filteredData.filter((i) => i.status === "dicek").length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <FaCheckCircle className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Bermasalah</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {
                    filteredData.filter((i) => i.status === "bermasalah")
                      .length
                  }
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-xl">
                <FaExclamationTriangle className="text-red-600 text-xl" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Selesai</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {filteredData.filter((i) => i.status === "selesai").length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <FaCheckCircle className="text-green-600 text-xl" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="w-full min-w-0 flex-1 xl:max-w-md">
              <SipSearch
                placeholder="Cari kategori, aset, layanan, petugas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:w-auto xl:flex-wrap">
              <SipDropdown
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="xl:w-44"
                options={[
                  { label: "Semua Status", value: "semua" },
                  { label: "Dicek", value: "dicek" },
                  { label: "Bermasalah", value: "bermasalah" },
                  { label: "Selesai", value: "selesai" },
                ]}
              />
              <SipDropdown
                value={filterTahun}
                onChange={(e) => setFilterTahun(e.target.value)}
                className="xl:w-40"
                options={[
                  { label: "Semua Tahun", value: "semua" },
                  ...getAvailableYears().map((year) => ({
                    label: String(year),
                    value: year.toString(),
                  })),
                ]}
              />
              <AddPerawatanIt
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                form={form}
                setForm={setForm}
                postData={postData}
                isEditMode={isEditMode}
                resetForm={resetForm}
                kategoriOptions={kategoriOptions}
                layananOptions={layananOptions}
                inventarisOptions={inventarisOptions}
                loadingOpsi={loadingOpsi}
                onKategoriChange={onKategoriChange}
                onInventarisSearch={onInventarisSearch}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <DataPerawatanIt
            rows={filteredData}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default PerawatanIt;
